import { signOut as firebaseSignOut } from "firebase/auth";
import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { getFirebaseAuth } from "@/HelloCashflowDomain/Authentication/services/firebase";
import memoizedAuth from "@/lib/firebase/MemoizedAuth";
import { setUID } from "@/redux/userSlice";
import { apiSlice } from "@/store/api/baseApi";
import { useAppDispatch } from "@/store/store";
import FrontendLoggerFactory from "@/utils/logging/FrontendLoggerFactory";

type UseAdminLogoutOptions = {
    redirectTo?: string;
};

/**
 * Centralized logout handler for the admin portal.
 *
 * @remarks
 * Firebase sign-out is simple (`signOut(auth)`), but we also reset in-memory client state to avoid
 * leaking cached RTK Query data between sessions (e.g. if a different user signs in without a full reload).
 */
export function useAdminLogout(options?: UseAdminLogoutOptions) {
    const redirectTo = options?.redirectTo ?? "/login";
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const logger = useMemo(
        () => FrontendLoggerFactory.getLogger().withComponent("useAdminLogout"),
        [],
    );

    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const logoutInFlightRef = useRef(false);

    const logout = useCallback(async () => {
        if (logoutInFlightRef.current) {
            return;
        }

        logoutInFlightRef.current = true;
        setIsLoggingOut(true);

        try {
            // Best practice: call Firebase's `signOut(auth)` (docs: https://firebase.google.com/docs/auth/web/password-auth#next_steps).
            // We defensively sign out both auth instances used in this codebase (domain DI + legacy wrapper).
            const signOutResults = await Promise.allSettled([
                (async () => firebaseSignOut(getFirebaseAuth()))(),
                memoizedAuth.signOut(),
            ]);

            const rejected = signOutResults.find(
                result => result.status === "rejected",
            );

            if (rejected && rejected.status === "rejected") {
                logger.warn("Sign out had one or more failures", {
                    operation: "sign_out_partial_failure",
                });
            }
        } catch (error) {
            logger.error(
                "Sign out failed",
                error instanceof Error ? error : undefined,
                { operation: "sign_out_failed" },
            );
        } finally {
            // Clear in-memory identity + cached API data before redirecting.
            dispatch(setUID(""));
            dispatch(apiSlice.util.resetApiState());
            logoutInFlightRef.current = false;
            setIsLoggingOut(false);
            navigate(redirectTo, { replace: true });
        }
    }, [dispatch, logger, navigate, redirectTo]);

    return { logout, isLoggingOut };
}
